import { useRef, useState } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import { uploadVideoFile } from './videoUpload';
import { useAdmin, uploadKey, isUploadActive } from '../admin/AdminStore';

// `hasHorizontal` means this video already has a horizontal file, so the only
// honest reading of another horizontal upload is 'replace' -- which belongs to
// ReplaceVideoFile, where it is confirmed and the derived data is handled.
// Vertical stays here: almost no video has one, so it is genuinely an add.
//
// Progress and outcome live in the store, not here: this panel unmounts every
// time the tab changes, and an upload that is invisible is an upload you start
// again. See AdminStore's upload registry. What stays local is the picked file,
// which cannot outlive the input that produced it anyway.
function VideoUploader({ identifier, onUploadSuccess, hasHorizontal = false }) {
  const { uploads, beginUpload, updateUpload, clearUpload } = useAdmin();
  const [files, setFiles] = useState({ horizontal: null, vertical: null });
  const lastPercent = useRef({});

  const entryFor = (kind) => uploads[uploadKey(identifier, kind)];
  // Any upload for this video holds both slots: they land on the same record.
  const busy = ['horizontal', 'vertical', 'replace'].some((k) => isUploadActive(entryFor(k)));

  const pick = (kind) => (event) => {
    // The label is disabled while an upload runs, but the input inside it is
    // still in the DOM -- and clearing a live entry would drop the very
    // progress this registry exists to keep, taking the guard against a second
    // upload with it.
    if (busy) return;
    setFiles((prev) => ({ ...prev, [kind]: event.target.files[0] || null }));
    // A new file supersedes whatever the last run of this slot reported.
    clearUpload(uploadKey(identifier, kind));
  };

  const upload = async (kind) => {
    const file = files[kind];
    const key = uploadKey(identifier, kind);
    // Belt and braces: the buttons are already disabled while one is running,
    // but starting a second upload of the same file is the failure this whole
    // registry exists to prevent.
    if (!file || busy) return;

    lastPercent.current[key] = 0;
    beginUpload(key, { identifier, kind, filename: file.name, state: 'uploading' });

    try {
      // Registering the assets used to hang off `onPlayable` alone, which the
      // SDK fires only after `upload()` has already resolved -- so it landed
      // too late to be awaited, and a finished upload could leave the record
      // with no file on it at all. The resolved response carries the same
      // assets; the callback is kept only as the earlier of the two.
      let hinted = null;
      const response = await uploadVideoFile(file, {
        // Written only when the whole percent changes. The raw event fires many
        // times a second, and every write here re-renders the workspace.
        onProgress: (progress) => {
          if (progress === lastPercent.current[key]) return;
          lastPercent.current[key] = progress;
          updateUpload(key, { progress });
        },
        onPlayable: (video) => {
          hinted = video.assets;
        },
      });
      const assets = hinted || response?.assets || null;

      if (!assets) {
        updateUpload(key, {
          state: 'error',
          message: `${kind} upload finished but api.video named no assets, so nothing was registered.`,
        });
        return;
      }

      updateUpload(key, { state: 'registering', progress: 100 });
      if (onUploadSuccess) await onUploadSuccess(assets, kind);
      updateUpload(key, {
        state: 'done',
        message: `${kind} upload complete. api.video may take a few minutes to finish every quality level.`,
      });
    } catch (error) {
      updateUpload(key, { state: 'error', message: `${kind} upload failed: ${error.message}` });
    }
  };

  const slot = (kind, label) => (
    <Box sx={{ mb: '18px' }}>
      <span className="admin-label">{label}</span>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', mt: '6px' }}>
        <Button component="label" disabled={busy}>
          Choose file
          <input
            hidden
            type="file"
            accept="video/*"
            aria-label={label}
            disabled={busy}
            onChange={pick(kind)}
          />
        </Button>
        <Button variant="contained" onClick={() => upload(kind)} disabled={!files[kind] || busy}>
          {isUploadActive(entryFor(kind)) ? 'Uploading…' : `Upload ${kind} video`}
        </Button>
      </Box>
      {files[kind] && (
        <Typography sx={{ fontSize: 11.5, color: 'var(--ps-text-dim)', mt: '4px' }}>
          {files[kind].name} ({(files[kind].size / 1024 / 1024).toFixed(1)} MB)
        </Typography>
      )}
    </Box>
  );

  return (
    <Box>
      {hasHorizontal ? (
        <Alert severity="info" sx={{ mb: '18px' }}>
          This video already has a horizontal file. Use “Replace video file” to swap it — that
          flow confirms the change and handles the subtitles and chapters timed against the
          current file.
        </Alert>
      ) : (
        slot('horizontal', 'Horizontal video')
      )}

      {slot('vertical', 'Vertical video (optional)')}

      {/* Progress and the outcome are rendered by the tab, from the store, so
          they survive this panel unmounting. */}
    </Box>
  );
}

export default VideoUploader;
